import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Validation schema for link form
const linkValidationSchema = Yup.object().shape({
    url: Yup.string()
        .required('URL is required')
        .matches(
            /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/i,
            'Please enter a valid URL (e.g., https://example.com or example.com)'
        ),
    title: Yup.string()
        .required('Title is required')
        .min(2, 'Title is required')
});

const LinkForm = ({
    mode = 'add',
    initialData = null,
    onSubmit,
    onDeleteLink = null,
    isLoading = false
}) => {
    const [deleteLoading, setDeleteLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            url: initialData?.url || '',
            title: initialData?.title || ''
        },
        validationSchema: linkValidationSchema,
        enableReinitialize: true,
        onSubmit: values => {
            onSubmit(values);
        }
    });

    const handleDeleteClick = async () => {
        if (onDeleteLink) {
            setDeleteLoading(true);
            try {
                await onDeleteLink();
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    return (
        <div className='pt-2'>
            {/* Title */}
            <h2 className='text-[24px] font-poppins font-semibold text-black mb-6 text-center'>
                {mode === 'edit' ? 'Edit Link' : 'Add Link'}
            </h2>

            <form onSubmit={formik.handleSubmit} className='space-y-6 px-4'>
                {/* Website URL Field */}
                <div>
                    <label
                        htmlFor='url'
                        className='block text-[16px] font-semibold text-black mb-2'
                    >
                        Website URL
                    </label>
                    <input
                        type='text'
                        id='url'
                        name='url'
                        placeholder='URL'
                        value={formik.values.url}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full px-4 py-5 text-black border-2 rounded-[12px] font-poppins focus:outline-none focus:border-blue-500 transition-colors ${
                            formik.touched.url && formik.errors.url
                                ? 'border-red-500'
                                : 'border-gray-300'
                        }`}
                    />
                    {formik.touched.url && formik.errors.url && (
                        <p className='text-red-500 text-[12px] mt-1 flex items-center gap-1'>
                            <span>▲</span>
                            <span className='italic'>{formik.errors.url}</span>
                        </p>
                    )}
                </div>

                {/* Website Title Field */}
                <div>
                    <label
                        htmlFor='title'
                        className='block text-[16px] font-semibold text-black mb-2'
                    >
                        Website Title
                    </label>
                    <input
                        type='text'
                        id='title'
                        name='title'
                        placeholder='Title'
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full px-4 py-5 text-black  border-2 rounded-[12px] font-poppins focus:outline-none focus:border-blue-500 transition-colors ${
                            formik.touched.title && formik.errors.title
                                ? 'border-red-500'
                                : 'border-gray-300'
                        }`}
                    />
                    {formik.touched.title && formik.errors.title && (
                        <p className='text-red-500 text-[12px] mt-1 flex items-center gap-1'>
                            <span>▲</span>
                            <span className='italic'>
                                {formik.errors.title}
                            </span>
                        </p>
                    )}
                </div>

                {/* Delete Link Button - Only in Edit Mode */}
                {mode === 'edit' && onDeleteLink && (
                    <div className='py-2'>
                        <button
                            type='button'
                            onClick={handleDeleteClick}
                            disabled={deleteLoading || isLoading}
                            className='w-full text-red-500 font-semibold text-[16px] hover:text-red-600 transition-colors py-2 border-none bg-transparent disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {deleteLoading ? 'Deleting...' : 'Delete Link'}
                        </button>
                    </div>
                )}

                {/* Confirm Button */}
                <div className='flex justify-center pt-6'>
                    <button
                        type='submit'
                        disabled={isLoading || deleteLoading}
                        className='bg-black text-white rounded-full px-12 py-3 font-semibold text-[16px] hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        {isLoading ? 'Saving...' : 'Confirm'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default LinkForm;
